"use client";

import React from "react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

interface ProductCardProps {
  product: {
    id: string;
    name: string;
    category: string;
    price: number;
    currency: string;
    image: string;
    alt: string;
    rating: number;
    reviews: number;
  };
}

export default function ProductCard({ product }: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const router = useRouter();

  const handleQuickView = () => {
    router.push(`/online-shop/${product.id}`);
  };

  return (
    <div
      className="group reveal will-animate"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="bg-card rounded-3xl overflow-hidden shadow-md hover-lift cursor-pointer" onClick={handleQuickView}>
        {/* Image */}
        <div className="relative aspect-square overflow-hidden">
          <AppImage
            src={product.image}
            alt={product.alt}
            className={`w-full h-full object-cover transition-transform duration-500 ${
              isHovered ? "scale-110" : "scale-100"
            }`}
          />
          {/* View Details Overlay */}
          <div
            className={`absolute inset-0 bg-foreground/50 flex items-center justify-center transition-opacity duration-300 ${
              isHovered ? "opacity-100" : "opacity-0"
            }`}
          >
            <span className="px-6 py-3 bg-white text-foreground rounded-full font-semibold text-sm hover:bg-white/90 transition-colors">
              View Details
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {product.category}
            </span>
          </div>
          <h3 className="font-semibold text-lg text-foreground mb-2 line-clamp-2">
            {product.name}
          </h3>

          {/* Rating */}
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Icon
                key={`star_${product.id}_${i}`}
                name="StarIcon"
                size={16}
                variant={i < Math.floor(product.rating) ? "solid" : "outline"}
                className={
                  i < Math.floor(product.rating) ? "text-warning" : "text-muted"
                }
              />
            ))}
            <span className="text-sm text-muted-foreground ml-1">
              ({product.reviews})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}